import dotProp from "dot-prop-immutable";
import ClientId from "data/ClientId";
import TableColumnMetaStore from "data/meta/TableColumnMetaStore";
import entityMetaStore from "data/meta/entityMetaStore";
import entityFieldMetaStore from "data/meta/entityFieldMetaStore";
import { optionId } from "data/selectors/options";
import { rehydrate } from "./hydration";
import TableProxy from "./TableProxy";
import EntityProxy, {
    hasCandidateFilter,
    hasEnableScript,
    hasRecalcScript,
    isAutoManaged,
    isChoice,
    isComposition,
} from "./EntityProxy";
import SuperRecordListStore from "data/SuperRecordListStore";
import cleanValue from "data/util/cleanValue";
import StereotypesStore from "../StereotypesStore";
import StereotypeProxy from "./StereotypeProxy";
import parseId from "data/util/parseId";
import asArray from "data/util/asArray";
import TableMetaStore from "data/meta/TableMetaStore";
import uniqueFilterBuilder from "data/util/uniqueFilterBuilder";
import { getTableKeys } from "data/selectors/stereotypes";

function areEqual(curr, next) {
    if (curr === next) return true;
    const isCurrArray = Array.isArray(curr);
    const isNextArray = Array.isArray(next);
    if (curr == null || next == null) {
        // if either one is null, use a coercive check. If coercion fixes it
        // that means one's null and one's undefined or something, which is
        // equal for our purposes.
        // noinspection EqualityComparisonWithCoercionJS
        if (curr == next) return true;
        // we also consider an empty array to be equal to null, following
        // Hibernate's "null/empty" semantics (for parity across the wire).
        if (next == null && isCurrArray && curr.length === 0) return true;
        if (curr == null && isNextArray && next.length === 0) return true;
    }
    if (isCurrArray && isNextArray && curr.length === next.length) {
        // only consider simple values in arrays (i.e., not deep equals).
        return curr.every((it, i) => it === next[i]);
    } else if (moment.isMoment(curr) && moment.isMoment(next)) {
        // if Moment's have the same value and timezone, consider them equal.
        // the `Moment.isSame` method is too new for us to use.
        return curr.valueOf() === next.valueOf()
            && curr.zone() === next.zone();
    }
    return false;
}

function updateIfNeeded(state, path, valueOrMutator) {
    const curr = dotProp.get(state, path);
    const next = typeof valueOrMutator === "function"
        ? valueOrMutator(curr)
        : valueOrMutator;
    return areEqual(curr, next)
        ? state
        : dotProp.set(state, path, next);
}

function chaseDownstream(entityProxies, downstream) {
    let currs = entityProxies;
    const parts = downstream.split(".");
    const targetColumnKey = parts.pop();
    for (const p of parts) {
        const nexts = [];
        for (const c of currs) {
            if (! c.frontsColumnWithKey(p)) {
                continue;
            }
            const v = c.getColumnByKey(p);
            if (v instanceof Array) {
                nexts.push(...v);
            } else if (v != null) {
                nexts.push(v);
            }
        }
        currs = nexts;
        if (currs.length === 0) break;
    }
    return [
        currs
            .filter(it => it.frontsColumnWithKey(targetColumnKey))
            .filter(uniqueFilterBuilder(it => it.getKey())),
        targetColumnKey
    ];
}

function buildFieldInfosByColumn(entityInfos, optionalFieldFilter = null) {
    const fieldInfosByColumn = {};
    for (const ei of entityInfos) {
        for (const f of entityFieldMetaStore.getAllInfos(ei.key, optionalFieldFilter)) {
            if (fieldInfosByColumn.hasOwnProperty(f.logicalColumn)) {
                fieldInfosByColumn[f.logicalColumn].push(f);
            } else {
                fieldInfosByColumn[f.logicalColumn] = [f];
            }
        }
    }
    return fieldInfosByColumn;
}

/*
 * I wrap up all the voodoo around a single record mutating action into a nice
 * stateful piece which is testable without Flux, and simplifies the Store(s)
 * it's needed in.
 *
 * In particular, pay careful attention to colName vs colKey (and their
 * shorthands cn and ck). Because of the way jsmodel works, most column-level
 * operations use names not keys, but it's not quite 100%.
 *
 * Many methods accepting a key (table, entity, field, etc.) will take an info
 * object as well. If you try and it doesn't work, consider adding the
 * capability to the method, rather than changing the callsite to dereference
 * the key.
 */
class TableAccess {

    constructor(state, readOnly) {
        if (!state) {
            throw new TypeError("TableAccess requires non-null state");
        }
        this.state = state;
        this.readOnly = !!readOnly;
        // Because automanagement will always end up changing a field that has
        // the auto-managed collection as a downstream, we have to break cycles.
        // This queue is how we do it.
        this.inFlightAutomanagement = new Set();
    }

    _ensureWriteable() {
        if (!this.readOnly) return;
        throw new Error("This TableAccess does not support mutation");
    }

    // direct-access methods
    // these are the _only_ methods allows to touch `this.state` in any way

    getState() {
        return this.state;
    }

    hasRecord(tableKey, id) {
        const tables = this.state.tables;
        return tables.hasOwnProperty(tableKey) && tables[tableKey].hasOwnProperty(id);
    }

    hasEntityRecord(entityKey, id) {
        const ei = entityMetaStore.getInfo(entityKey);
        return this.hasRecord(ei.tableKey, id);
    }

    getRecordData(tableKey, id) {
        if (! this.hasRecord(tableKey, id)) {
            console.warn("Data requested for non-existant record", tableKey, id);
            return null;
        }
        return this.state.tables[tableKey][id];
    }

    getAllRecords(tableKey) {
        // todo: gross. don't do this. index it for callsites to use
        const tables = this.state.tables;
        if (! tables.hasOwnProperty(tableKey)) {
            // This isn't a problem. It's reasonable to request all top-level
            // records before any have been created, for example. But it's weird
            // enough that seeing a trace during dev is useful
            if (process.env.NODE_ENV === "development") {
                console.warn(`Records requested for non-existent '${tableKey}' table`);
            }
            return [];
        }
        return Object.values(tables[tableKey]);
    }

    _setRecord(tableKey, id, fullRecord) {
        this._ensureWriteable();
        this.state = dotProp.set(this.state, ["tables", tableKey, id], fullRecord);
    }

    // really "ensure column has value", and returns whether a change was made
    _setColumn(tableKey, id, colName, valueOrMutator) {
        this._ensureWriteable();
        const path = ["tables", tableKey, id, "data", colName];
        const prev = this.state;
        this.state = updateIfNeeded(prev, path, valueOrMutator);
        return this.state !== prev;
    }

    _getColumn(tableKey, id, colName) {
        return dotProp.get(this.state, ["tables", tableKey, id, "data", colName]);
    }

    _deleteRecord(tableKey, id) {
        this._ensureWriteable();
        this.state = dotProp.delete(this.state, ["tables", tableKey, id]);
    }

    _enqueueListHandler(entityKey, handler) {
        this._ensureWriteable();
        this.state = dotProp.set(this.state, ["listQueue", entityKey], v => {
            v = (v instanceof Array) ? v.slice() : [];
            v.push(handler);
            return v;
        });
    }

    _dequeueListHandlers(entityKey) {
        this._ensureWriteable();
        const queue = this.state.listQueue;
        const handlers = queue[entityKey];
        if (handlers != null) {
            this.state = dotProp.delete(this.state, ["listQueue", entityKey]);
        }
        return handlers || [];
    }

    // end direct access methods

    getColumnData(tableKey, id, colName) {
        const r = this.getRecordData(tableKey, id);
        if (r == null) return null;
        if (process.env.NODE_ENV !== "production") {
            if (!r.data.hasOwnProperty(colName)) {
                console.warn(
                    "Column data requested for unknown column",
                    tableKey,
                    id,
                    colName,
                );
            }
        }
        return r.data[colName];
    }

    getEntityProxy(entityKey, id) {
        const info = entityMetaStore.getInfo(entityKey);
        return new EntityProxy(this, info, id);
    }

    getStereotypeProxy(stereoKey, id) {
        const info = StereotypesStore.getStereotypeByKey(stereoKey);
        return new StereotypeProxy(this, info, id);
    }

    loadRecordData(tableKey, id, meta, data) {
        this._setRecord(tableKey, id, {
            meta: {
                ...meta,
                id,
                waiting: false,
            },
            data,
        });
        return this;
    }

    loadSuperRecordPacket(packet, ofEntityKey = null) {
        // load all the data in
        const idsPerTable = {};
        const allIds = new Set();
        for (const tableKey of Object.keys(packet.tables)) {
            const rs = packet.tables[tableKey];
            idsPerTable[tableKey] = [];
            for (const idString of Object.keys(rs)) {
                const id = parseId(idString);
                idsPerTable[tableKey].push(id);
                allIds.add(id);
            }
        }
        for (const tableKey of Object.keys(packet.tables)) {
            const rs = packet.tables[tableKey];
            for (const idString of Object.keys(rs)) {
                const id = parseId(idString);
                const data = {
                    ...rs[id],
                };
                const ts = data._ts;
                const cts = data._cts;
                delete data._id;
                delete data._ts;
                delete data._cts;
                const colInfos = TableColumnMetaStore.getAllColumnsByName(tableKey);
                // cull any bad assoc FKs
                for (const n of Object.keys(colInfos)) {
                    if (colInfos[n].type !== "ASSOCIATION") continue;
                    if (data[n] == null) continue;
                    if (_.isArray(data[n])) {
                        data[n] = data[n].filter(it => allIds.has(it));
                    } else if (!allIds.has(data[n])) {
                        data[n] = null;
                    }
                }
                this.loadRecordData(
                    tableKey,
                    id,
                    {ts, cts},
                    rehydrate(data, tableKey, false, colInfos),
                );
            }
        }
        const newIdMirror = {};
        if (packet.newIds) {
            Object.keys(packet.newIds).forEach(oid =>
                newIdMirror[packet.newIds[oid]] = oid);
        }
        // initialize all the records
        for (const tableKey of Object.keys(idsPerTable)) {
            const writable = TableMetaStore.canWrite(tableKey);
            for (const id of idsPerTable[tableKey]) {
                if (newIdMirror.hasOwnProperty(id) && this.hasRecord(tableKey, newIdMirror[id])) {
                    this._deleteRecord(tableKey, newIdMirror[id]);
                }
                if (writable && this.hasRecord(tableKey, id)) {
                    this.initializeTableRecord(tableKey, id, ofEntityKey, ofEntityKey != null);
                }
            }
        }
        return this;
    }

    initializeTableRecord(tableKey, id, ofEntityKey = null, filterEntityInfos = false) {
        // first derivations
        const allCols = TableColumnMetaStore.getAllColumnsByName(tableKey);
        const proxy = new TableProxy(this, tableKey, id, true);
        for (const n of Object.keys(allCols)) {
            const info = allCols[n];
            const d = info.derivation;
            if (d == null) continue;
            if (d.sourceKey == null && proxy.getColumn(info.key) != null) {
                // has a value already, skip it
                continue;
            }
            this._doDerivation(proxy, d);
        }

        // now do entity stuff....
        const isNew = ClientId.is(id);
        const entityInfos = entityMetaStore.getInfosForLogicalTable(tableKey, filterEntityInfos === true ? ofEntityKey : null);
        if (ofEntityKey != null) {
            entityInfos.sort((a, b) => {
                if (a.key === ofEntityKey) return -1;
                if (b.key === ofEntityKey) return +1;
                return 0;
            });
        }

        // recalcs
        // todo: materialize this index
        const recalcsByColumn = buildFieldInfosByColumn(
            entityInfos,
            hasRecalcScript,
        );
        if (isNew) {
            // all of them!
            for (const colName of Object.keys(recalcsByColumn)) {
                this.recalculateTableRecordColumn(
                    tableKey,
                    id,
                    colName,
                    recalcsByColumn[colName],
                );
            }
        } else {
            // only updateable, non-null ones
            for (const colName of Object.keys(recalcsByColumn)) {
                const v = this.getColumnData(tableKey, id, colName);
                if (v != null && (!(v instanceof Array) || v.length !== 0)) {
                    continue;
                }
                const infos = recalcsByColumn[colName];
                if (infos.some(f => f.nonUpdateable)) continue;
                this.recalculateTableRecordColumn(tableKey, id, colName, infos);
            }
        }

        // auto-management
        // todo: materialize this index
        const autoManageByColumn = buildFieldInfosByColumn(
            entityInfos,
            isAutoManaged,
        );
        if (Object.keys(autoManageByColumn).length > 0) {
            // const lo = SuperRecordListStore.getList()
            for (const fis of Object.values(autoManageByColumn)) {
                for (const fi of fis) {
                    this._requestAutoManagement(tableKey, id, fi);
                }
            }
        }

        return this;
    }

    _requestAutoManagement(tableKey, id, fieldInfo) {
        if (fieldInfo.autoManageKey == null) throw new Error(fieldInfo.key + " isn't auto-managed");
        const mfi = entityFieldMetaStore.getInfo(
            fieldInfo.otherEntityKey,
            fieldInfo.autoManageKey,
        );
        const lo = SuperRecordListStore.getListLO(mfi.otherEntityKey);
        if (lo.hasValue()) {
            this._doAutoManagement(
                tableKey,
                id,
                fieldInfo,
                mfi,
                lo.getRequiredValue(),
            );
        } else {
            this._enqueueListHandler(mfi.otherEntityKey, (access, ids) => {
                if (access.hasRecord(tableKey, id)) {
                    access._doAutoManagement(tableKey, id, fieldInfo, mfi, ids);
                }
            });
        }
    }

    processListQueue(entityKey, ids) {
        for (const h of this._dequeueListHandlers(entityKey)) {
            h(this, ids);
        }
    }

    _buildEnableState(tableKey, id, downstreams) {
        const result = {
            // table#id#colKey: enabled
        };
        this._forEachDownstream(tableKey, id, downstreams, (entityProxies, colKey) => {
            if (entityProxies.length === 0) return; // nothing to do
            entityFieldMetaStore.getInfosForColumnKey(
                entityProxies.map(p => p.entityKey),
                colKey,
            )
                .filter(it => hasEnableScript(it))
                .flatMap(fieldInfo => entityProxies
                    .filter(ep =>
                        ep.entityKey === fieldInfo.entityKey)
                    .map(ep => ({
                        tableKey: ep.tableProxy.getKey(),
                        enabled: ep.isFieldEnabled(fieldInfo),
                    })))
                .forEach(it =>
                    result[it.tableKey + "#" + colKey] = it.enabled);
        });
        return result;
    }

    // the magic edge is backwards, so we get the child's type, not the parent's
    createMagicChild(id, entityKey, newId) {
        this.createEntityRecord(entityKey, newId, false);
        const e = entityMetaStore.getInfo(entityKey);
        this.updateEntityRecordField(entityKey, newId, e.magicRootPointerKey, id);
        this.initializeTableRecord(e.tableKey, newId, entityKey);
        return this;
    }

    createEntityRecord(entityKey, newId, initialize = true) {
        const e = entityMetaStore.getInfo(entityKey);
        this.createTableRecord(e.tableKey, newId, entityKey, initialize);
        return this;
    }

    createEntityChild(entityKeyOrInfo, id, fieldKeyOrInfo, newId, insertAt) {
        const ei = typeof entityKeyOrInfo === "string"
            ? entityMetaStore.getInfo(entityKeyOrInfo)
            : entityKeyOrInfo;
        const fi = typeof fieldKeyOrInfo === "string"
            ? entityFieldMetaStore.getInfo(ei.key, fieldKeyOrInfo)
            : fieldKeyOrInfo;
        this.createEntityRecord(fi.otherEntityKey, newId, false);
        const colInfo = TableColumnMetaStore.getInfoByKey(
            ei.tableKey,
            fi.logicalKey,
        );
        this.wireTableChild(ei.tableKey, id, colInfo, newId, insertAt);
        this.initializeTableRecord(
            colInfo.otherTableKey,
            newId,
            fi.otherEntityKey,
        );
        return this;
    }

    createTableRecord(tableKey, newId, ofEntityKey = null, initialize = true) {
        this.loadRecordData(
            tableKey,
            newId,
            {},
            rehydrate({}, tableKey),
        );
        if (initialize) {
            this.initializeTableRecord(tableKey, newId, ofEntityKey);
        }
        return this;
    }

    createTableChild(tableKey, id, colName, newId, insertAt) {
        const colInfo = TableColumnMetaStore.getInfoByName(tableKey, colName);
        this.createTableRecord(colInfo.otherTableKey, newId);
        this.wireTableChild(tableKey, id, colInfo, newId, insertAt);
        return this;
    }

    deleteEntityRecord(entityKey, id) {
        const e = entityMetaStore.getInfo(entityKey);
        this.deleteTableRecord(e.tableKey, id);
        return this;
    }

    deleteEntityChild(entityKeyOrInfo, id, fieldKeyOrInfo, childId) {
        const ei = typeof entityKeyOrInfo === "string"
            ? entityMetaStore.getInfo(entityKeyOrInfo)
            : entityKeyOrInfo;
        const fi = typeof fieldKeyOrInfo === "string"
            ? entityFieldMetaStore.getInfo(ei.key, fieldKeyOrInfo)
            : fieldKeyOrInfo;
        this.deleteTableChild(ei.tableKey, id, fi.logicalColumn, childId);
        return this;
    }

    deleteTableRecord(tableKey, id) {
        if (!this.hasRecord(tableKey, id)) return this;
        const tp = new TableProxy(this, tableKey, id);
        const entityInfos = tp.getEntityKeys()
            .map(ek => entityMetaStore.getInfo(ek));
        for (const ei of entityInfos) {
            // ON DELETE CASCADE for compositions
            for (const comp of ei.associations.csv) {
                const efi = entityFieldMetaStore.getInfo(ei.key, comp);
                const v = this.getColumnData(tableKey, id, efi.logicalColumn);
                if (v != null) {
                    this.deleteEntityRecord(efi.otherEntityKey, v);
                }
            }
            for (const comp of ei.associations.cmv) {
                const efi = entityFieldMetaStore.getInfo(ei.key, comp);
                const v = this.getColumnData(tableKey, id, efi.logicalColumn);
                if (v != null && v.length !== 0) {
                    for (const id of v) {
                        this.deleteEntityRecord(efi.otherEntityKey, id);
                    }
                }
            }

            // unwireChildCrossTreeRefs() works from the child out but here we
            // need to work in the other direction. Leaving this for now.
            // ON DELETE SET NULL for cross-tree associations
            for (const inv of ei.associations.inv) {
                const ifi = entityFieldMetaStore.getInfo(ei.key, inv);
                const efi = entityFieldMetaStore.getInfo(
                    ifi.otherEntityKey,
                    ifi.inverseKey
                );
                if (efi.type === "CompositionField") {
                    continue;
                }
                const ownerIds = tp.get(ifi.logicalColumn);
                if (ownerIds == null || ownerIds.length === 0) {
                    continue;
                }
                const iei = entityMetaStore.getInfo(efi.entityKey);
                const ci = TableColumnMetaStore.getInfoByKey(
                    iei.tableKey,
                    efi.logicalKey,
                );
                for (const oid of ownerIds) {
                    this.unwireTableChild(iei.tableKey, oid, ci, id);
                }
            }
        }
        this._deleteRecord(tableKey, id);
        return this;
    }

    deleteTableChild(tableKey, id, colNameOrInfo, childId) {
        const colInfo = typeof colNameOrInfo === "string"
            ? TableColumnMetaStore.getInfoByName(tableKey, colNameOrInfo)
            : colNameOrInfo;
        this.unwireTableChild(tableKey, id, colInfo, childId);
        this.unwireChildCrossTreeRefs(colInfo, childId);
        return this;
    }

    _doAutoManagement(tableKey, id, childInfo, globalInfo, allIds) {
        /*
         * tableKey.id owns a composed collection, described by childInfo. One
         * of the fields on the child type is a global association described by
         * globalInfo. The IDs of the global records are in allIds.
         *
         * The task is to populate the composed collection with one record for
         * every global record which passes the global association's candidate
         * filter. This includes removing children who no longer have a global
         * record. It is against the rules for the filter expression to
         * reference the child record itself, excepting to get up to the parent
         * record (referenced by tableKey.id).
         */
        const ifamKey = id + ":" + tableKey; // id will differentiate earlier
        if (this.inFlightAutomanagement.has(ifamKey)) return;
        this.inFlightAutomanagement.add(ifamKey);
        try {
            // create a fake child record to use for candidate filtering the global
            // list, with fail-fast-on-silly behaviour.
            const ownerTP = new TableProxy(this, tableKey, id);
            const ownerEP = new EntityProxy(ownerTP, childInfo.entityKey);
            if (! ownerEP.isFieldEnabled(childInfo)) return;
            const childTableKey = entityMetaStore.getInfo(childInfo.otherEntityKey).tableKey;
            const globalTableKey = entityMetaStore.getInfo(globalInfo.otherEntityKey).tableKey;
            const fakeChild = {
                get(name) {
                    if (name === childInfo.inverseKey) {
                        return ownerEP;
                    }
                    throw new Error("Auto-management candidate filters can only reference the instance's parent field");
                }
            };

            // todo: we need to care about enablement somewhere?
            let filteredIds;
            if (hasCandidateFilter(globalInfo)) {
                // inflate ids to EntityProxies
                let eps = allIds.map(id =>
                    new EntityProxy(this, globalInfo.otherEntityKey, globalTableKey, id));
                // run candidate filter
                const filter = globalInfo.scripts.filter;
                eps = eps.filter(it => filter(fakeChild, it));
                // back to ids
                filteredIds = eps.map(it => it.id);
            } else {
                filteredIds = allIds;
            }
            // now we know the set of global IDs we need to have a child for!

            // get the current children
            const currChildIds = ownerTP.get(childInfo.logicalColumn);
            const currs = currChildIds.map(id => ({
                child: id,
                global: this.getColumnData(childTableKey, id, globalInfo.logicalColumn),
            }));
            // for each child
            for (const curr of currs) {
                // if its AMF's id is not filteredIds, delete
                if (filteredIds.indexOf(curr.global) >= 0) continue;
                this.deleteEntityChild(ownerEP.entityKey, ownerEP.id, childInfo.key, curr.child);
            }
            // for each newId
            for (const id of filteredIds) {
                // if its has no child w/ that AMF id, create
                if (currs.some(it => it.global === id)) continue;
                const newId = ClientId.next();
                this.createEntityChild(ownerEP.entityKey, ownerEP.id, childInfo.key, newId);
                this.updateTableRecordColumn(childTableKey, newId, globalInfo.logicalColumn, id);
            }
        } finally {
            this.inFlightAutomanagement.delete(ifamKey);
        }
    }

    _doDerivation(tableProxy, derivation) {
        const info = TableColumnMetaStore.getInfoByKey(
            tableProxy.tableKey,
            derivation.targetKey,
        );
        let value = derivation.script(tableProxy);
        if (value != null && info.type === "CHOICE") {
            value = optionId(derivation.choiceKey)(value);
        }
        this.updateTableRecordColumn(
            tableProxy.tableKey,
            tableProxy.id,
            derivation.targetName,
            value,
            info,
        );
        return this;
    }

    _doDerivations(tableKey, id, derivations) {
        if (derivations.length === 0) return this;
        // these are simpler than downstreams, because they're entirely at the table
        // level, and they're not allowed to traverse associations.
        const proxy = new TableProxy(this, tableKey, id, true);
        for (const d of derivations) {
            this._doDerivation(proxy, d);
        }
        return this;
    }

    _doDownstreamEnablements(tableKey, id, downstreams, priorEnableState) {
        if (downstreams.length === 0) return this;
        const enableState = this._buildEnableState(
            tableKey,
            id,
            downstreams,
        );
        for (const k of Object.keys(enableState)) {
            const enabled = enableState[k];
            const wasEnabled = priorEnableState.hasOwnProperty(k) && priorEnableState[k];
            const [tableKey, idString, colKey] = k.split("#");
            const id = parseId(idString);
            const info = TableColumnMetaStore.getInfoByKey(tableKey, colKey);
            if (!enabled && wasEnabled) {
                // became disabled
                this.updateTableRecordColumn(tableKey, id, info.name, info.multiValued ? [] : null, info);
            } else if (enabled && !wasEnabled) {
                // became enabled
                const fieldInfos = entityFieldMetaStore.getInfosForColumn(
                    new TableProxy(this, tableKey, id).getEntityKeys(),
                    info.name
                );
                const toRecalc = fieldInfos.filter(hasRecalcScript);
                if (toRecalc.length) {
                    this.recalculateTableRecordColumn(tableKey, id, info.name, toRecalc, false);
                }
                if (info.type === "ASSOCIATION") {
                    for (const fi of fieldInfos) {
                        if (fi.autoManageKey) {
                            this._requestAutoManagement(tableKey, id, fi);
                            break; // first one wins
                        }
                    }
                }
            }
        }
        return this;
    }

    _doDownstreamRecalcs(tableKey, id, downstreams) {
        // todo: does this need to do an enabled check?
        if (downstreams.length === 0) return this;
        this._forEachDownstream(tableKey, id, downstreams, (eps, colKey) => {
            const ep = eps.find(ep => {
                const entityKey = ep.entityKey;
                const fieldInfo = entityFieldMetaStore.getInfoForColumnKey(entityKey, colKey);
                // this is, perhaps, a little generous. But if we hit it, the
                // metamodel is inconsistent, so unlikely we can guess right
                // anyway. But do something, at least, not nothing (per HJC-430)
                return fieldInfo.calculated;
            });
            if (!ep) return;
            const tableKey = ep.tableProxy.tableKey;
            const targetColumnName = TableColumnMetaStore.getInfoByKey(tableKey, colKey).name;
            for (const p of eps) {
                this.recalculateTableRecordColumn(tableKey, p.id, targetColumnName);
            }
        });
        return this;
    }

    _doDownstreamAutoManagement(tableKey, id, downstreams) {
        // todo: does this need to do an enabled check?
        if (downstreams.length === 0) return this;
        this._forEachDownstream(tableKey, id, downstreams, (eps, colKey) => {
            if (eps.length === 0) return;
            const tableKey = eps[0].tableProxy.tableKey;
            const colInfo = TableColumnMetaStore.getInfoByKey(tableKey, colKey);
            const targetColumnName = colInfo.name;
            for (const p of eps) {
                const fieldInfo = entityFieldMetaStore.getInfoForColumn(p.entityKey, targetColumnName);
                if (fieldInfo.autoManageKey == null) return;
                this._requestAutoManagement(tableKey, p.id, fieldInfo);
            }
        });
    }

    _forEachDownstream(tableKey, id, downstreams, work) {
        if (!this.hasRecord(tableKey, id)) return;
        const initialEps = new TableProxy(this, tableKey, id).getEntityProxies();
        for (const ds of downstreams) {
            const [eps, targetColumnKey] = chaseDownstream(initialEps, ds);
            if (eps.length === 0) continue;
            work(eps, targetColumnKey);
        }
    }

    _gatherDownstreams(tableKey, id, colInfo) {
        // todo: respect the dependency-ordered field tree?
        const derivations = colInfo.derivations || [];
        const downstreams = colInfo.downstreams || {};
        const enableDownstreams = downstreams.ENABLEMENT || [];
        const assignDownstreams = downstreams.ASSIGNMENT || [];
        const filterDownstreams = downstreams.CANDIDATE_FILTER || [];
        // todo: index these?
        const autoManageDownstreams = filterDownstreams
            .map(it => it.split("."))
            .filter(it => it.length >= 2)
            .map(it => {
                it.pop();
                return it.join(".");
            });
        let enableState;
        if (enableDownstreams.length > 0) {
            enableState = this._buildEnableState(
                tableKey,
                id,
                enableDownstreams,
            );
        }
        return {execute: () => {
            if (derivations.length > 0) {
                this._doDerivations(
                    tableKey,
                    id,
                    derivations,
                );
            }
            if (enableDownstreams.length > 0) {
                this._doDownstreamEnablements(
                    tableKey,
                    id,
                    enableDownstreams,
                    enableState,
                );
            }
            if (assignDownstreams.length > 0) {
                this._doDownstreamRecalcs(
                    tableKey,
                    id,
                    assignDownstreams,
                );
            }
            if (autoManageDownstreams.length > 0) {
                this._doDownstreamAutoManagement(
                    tableKey,
                    id,
                    autoManageDownstreams,
                );
            }
        }};
    }

    appendAssociationIds(tableKey, ids, targetId, columnNameOrInfo) {
        const columnInfo = typeof columnNameOrInfo === "string"
            ? TableColumnMetaStore.getInfoByName(tableKey, columnNameOrInfo)
            : columnNameOrInfo;
        if(columnInfo.type !== "ASSOCIATION") {
            throw new Error("Expected type to be an association.");
        }
        if(!Array.isArray(ids)) {
            throw new Error(`Expected ids to be an array of Ids but received "${typeof ids}"`);
        }
        // rewire ids to target
        ids.forEach(id =>
            this.wireTableChild(tableKey, targetId, columnInfo, id)
        );
        return this;
    }

    moveAssociationIds(tableKey, sourceId, targetId, columnNameOrInfo) {
        const columnInfo = typeof columnNameOrInfo === "string"
            ? TableColumnMetaStore.getInfoByName(tableKey, columnNameOrInfo)
            : columnNameOrInfo;
        const ids = this._getColumn(tableKey, sourceId, columnInfo.name); // get source ids
        this.appendAssociationIds(tableKey, ids, targetId, columnInfo); // rewire source ids
        this._setColumn(tableKey, sourceId, columnInfo.name, []); // empty source
        return this;
    }

    recalculateTableRecordColumn(tableKey, id, columnName, optionalFieldInfos, checkEnablement = true) {
        // todo: consider a contextual preference (an ofEntityKey)
        const tableProxy = new TableProxy(this, tableKey, id);
        const fieldInfos = (optionalFieldInfos == null
            ? entityFieldMetaStore.getInfosForColumn(tableProxy.getEntityKeys(), columnName)
            : optionalFieldInfos).filter(hasRecalcScript);
        if (fieldInfos.length === 0) {
            if (optionalFieldInfos != null) {
                console.warn("Recalc requested for scriptless column", tableKey, id, columnName);
            }
            return this;
        }


        // jsmodel broke these ties via "whatever order the change listeners
        // were registered in", because that was the only choice. it's also
        // completely non-obvious that that's the behaviour unless you dig deep
        // and exhibit great care during your travels. We'll be just as
        // arbitrary, but at least we're obvious about it. We'll also ensure the
        // _same_ info is picked every time, rather than potentially changing as
        // new metadata is loaded into the running app.
        const info = fieldInfos[0]; // FIRST IS BEST!
        // except this one. this one is special. :)
        const nonUpdateable = fieldInfos.some(it => it.nonUpdateable);


        const proxy = new EntityProxy(tableProxy, info.entityKey);
        if (checkEnablement && ! proxy.isFieldEnabled(info)) return this;
        const curr = proxy.getField(info.key);
        if (nonUpdateable && curr != null) return this;
        const newVal = info.scripts.recalc(proxy);
        this.updateEntityRecordField(info.entityKey, id, info.key, newVal);
        return this;
    }

    updateEntityRecordField(entityKey, id, fieldKey, value) {
        const entityInfo = entityMetaStore.getInfo(entityKey);
        const fieldInfo = entityFieldMetaStore.getInfo(entityKey, fieldKey);
        if (isChoice(fieldInfo)) {
            const nameToId = optionId(fieldInfo.choiceKey);
            if (value instanceof Array) {
                value = value.map(nameToId)
                    .filter(it => it != null);
            } else if (value != null) {
                value = nameToId(value);
            }
        }
        this.updateTableRecordColumn(
            entityInfo.tableKey,
            id,
            fieldInfo.logicalColumn,
            value,
        );
        return this;
    }

    addToEntityCollection(entityKey, id, fieldKey, valueToAdd) {
        const entityInfo = entityMetaStore.getInfo(entityKey);
        const fieldInfo = entityFieldMetaStore.getInfo(entityKey, fieldKey);
        const oldValue = this._getColumn(entityInfo.tableKey, id, fieldInfo.logicalColumn);
        if(oldValue && !Array.isArray(oldValue)) {
            console.error(`Expected oldValue to be null or an array but received "${typeof oldValue}".`, oldValue);
        }
        const newValue = !oldValue ? [] : [...oldValue]; // always return new array
        newValue.push(valueToAdd);
        return newValue;
    }

    updateTableRecordColumn(
        tableKey,
        id,
        colName,
        valueOrMutator,
        optionalColumnInfo, // do this default manually to avoid no-op updates
    ) {
        if (! this.hasRecord(tableKey, id)) {
            console.error(`update ignored for unknown record '${tableKey}#${id}'`, colName, valueOrMutator);
            return this;
        }

        const colInfo = optionalColumnInfo != null
            ? optionalColumnInfo
            : TableColumnMetaStore.getInfoByName(tableKey, colName);
        const oldValue = this._getColumn(tableKey, id, colName);
        const newValue = cleanValue(typeof valueOrMutator === "function"
            ? valueOrMutator(oldValue)
            : valueOrMutator, colInfo.type);

        // ignore no-op updates
        if (areEqual(oldValue, newValue)) return this;

        if (colInfo.inverse) {
            console.error(`Trying to set an inverse: ${colInfo.tableKey}.${colInfo.key} (.${colInfo.name})`);
        }

        // calc downstreams so we have pre-update enable state
        const downstreams = this._gatherDownstreams(
            tableKey,
            id,
            colInfo,
        );
        let inverseDownstreams = null;
        if (colInfo.type === "ASSOCIATION") {
            const removedIds = asArray(oldValue)
                .filter(newValue instanceof Array
                    ? v => newValue.indexOf(v) < 0
                    : v => v !== newValue);

            // if this column stores composition references, delete orphaned records
            const fieldInfos = entityFieldMetaStore.getInfosForColumn(
                new TableProxy(this, tableKey, id).getEntityKeys(),
                colName
            );
            const isComp = fieldInfos.some(isComposition);
            if (isComp) {
                removedIds.forEach(id =>
                    this.deleteTableRecord(colInfo.otherTableKey, id));
            }

            if (colInfo.hasOwnProperty("inverseKey") && colInfo.inverseKey) {
                const invInfo = TableColumnMetaStore.getInfoByKey(
                    colInfo.otherTableKey,
                    colInfo.inverseKey,
                );
                let ids = asArray(newValue);
                if (!isComp) {
                    ids = ids.concat(...removedIds);
                }
                inverseDownstreams = ids.map(v => this._gatherDownstreams(
                    colInfo.otherTableKey,
                    v,
                    invInfo,
                ));
            }
        }

        // actually do the set
        const changed = this._setColumn(tableKey, id, colName, newValue);
        if (!changed) {
            // per helen's decree, a no-op change does NOT cause the dependency
            // cascade to happen. if the user wants the cascade to fire without
            // changing a field, they need to make two changes: one to some
            // garbage value, and a second back to the original.
            return;
        }

        // do the downstreams, based on the pre-update state gathered above
        downstreams.execute();
        inverseDownstreams && inverseDownstreams.forEach(it => it.execute());
        return this;
    }

    wireTableChild(
        tableKey,
        id,
        colInfo,
        newId,
        insertAt,
    ) {
        this.updateTableRecordColumn(
            tableKey,
            id,
            colInfo.name,
            colInfo.multiValued ? v => {
                if (v == null) return [newId];
                v = v.slice();
                if (insertAt == null) v.push(newId);
                else v.splice(insertAt, 0, newId);
                return v;
            } : newId,
            colInfo,
        );
        return this;
    }

    unwireTableChild(tableKey, id, colInfo, childId) {
        this.updateTableRecordColumn(
            tableKey,
            id,
            colInfo.name,
            colInfo.multiValued ? v => {
                if (v == null) return v;
                const i = v.indexOf(childId);
                if (i < 0) return v;
                v = v.slice();
                v.splice(i, 1);
                return v;
            } : v => v === childId ? null : v,
            colInfo,
        );
        return this;
    }

    unwireChildCrossTreeRefs(colInfo, childId) {
        // find all table columns referencing inverse key
        const assocCols = TableColumnMetaStore.getAllColumns(colInfo.otherTableKey)
            .filter((it) => (
                it.type === "ASSOCIATION"
                && !!it.inverseKey
                && it.key !== colInfo.inverseKey
            ));
        // cleanup any remaining references to the just deleted childId.
        assocCols.forEach((_assocColInfo) => {
            // get all ID's for otherTableKey
            const ids = this.getAllRecords(_assocColInfo.otherTableKey)
                .map(_assocColInfo => _assocColInfo.meta.id);
            ids.forEach(_id => {
                const _colInfo = TableColumnMetaStore
                    .getInfoByKey(_assocColInfo.otherTableKey, _assocColInfo.inverseKey);
                this.unwireTableChild(_assocColInfo.otherTableKey, _id, _colInfo, childId);
            });
        });
        return this;
    }

    remapGlobalMergeTargetToSource(target, source) {
        const backedTables = getTableKeys(
            StereotypesStore.getStereotypeByKey(target.stereoKey));
        if(backedTables.length !== 1) {
            // how did we get here... can only merge single table backed stereotypes.
            throw new Error("Merge only supports single table backed stereotypes.");
        }
        const tableKey = backedTables[0];
        // global merge may have source id associations that need to be remapped
        // to the target id after merge.

        // 1. first get all inverse association fields from TableColumnMetaStore.
        const tableKeys = Object.keys(this.state.tables);
        const assocCols = TableColumnMetaStore.getAllColumns(tableKey)
            .filter((it) => (
                it.type === "ASSOCIATION"
                && !!it.inverseKey
                && tableKeys.includes(it.otherTableKey) // we only care about the records currently loaded
            ));

        // 2. loop over all associations and update each record that referenced
        // the target id and change it to the source id.
        assocCols.forEach(it => {
            const columnInfo = TableColumnMetaStore.getInfoByKey(it.otherTableKey, it.inverseKey);
            const ids = this.getAllRecords(it.otherTableKey)
                .map(_assocColInfo => _assocColInfo.meta.id);
            ids.forEach((id) => {
                const value = this.getColumnData(it.otherTableKey, id, columnInfo.name);
                let needsUpdate = false;
                let newValue;
                if(Array.isArray(value)) {
                    const idx = value.indexOf(source.id);
                    if(idx >= 0) {
                        needsUpdate = true;
                        newValue = [...value];
                        newValue[idx] = target.id;
                    }
                }
                else {
                    if(value === source.id) {
                        needsUpdate = true;
                        newValue = target.id;
                    }
                }
                if(needsUpdate) {
                    this.updateTableRecordColumn(
                        it.otherTableKey,
                        id,
                        columnInfo.name,
                        newValue
                    );
                }
            });
        });
        return this;
    }
}

export default TableAccess;



// WEBPACK FOOTER //
// ./app/data/util/TableAccess.js